import "./ArtistNetwork.scss";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Col,
  Row,
  Select,
  Slider,
  InputNumber,
  Typography,
  Table,
  Tag,
  Radio,
  Tooltip,
  message,
} from "antd";
import { EyeOutlined, LeftOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { fetchArtistGraph } from "radar/Service/RadarService";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ArtistGraphThree, {
  ControlSection,
  ControlsPanel,
  Drawer,
} from "shared/components/artist-graph-three";
import ArtistGraphCanvas from "./ArtistGraphCanvas";
import {
  RADAR_ARTIST_RANKING_FILTER,
  RADAR_ARTIST_RANKING_INDEX,
} from "shared/constants/LocalStorageConsts";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { unstable_batchedUpdates } from "react-dom";
import { motion } from "framer-motion";
import moment from "moment";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import { ptBRFormat } from "shared/helpers/BRLFormat";
import { ArtistsRankingIndex } from "explorer/reducers/rankings";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { UserTypeEnum } from "shared/enums/UserTypeEnum";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";

function detectWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    
    // Precisamos testar exatamente o que o Three.js pede lá no seu motor 3D
    const options: WebGLContextAttributes = { 
      failIfMajorPerformanceCaveat: true, // Se for performance de software, falhe.
      antialias: true, 
      alpha: true 
    };

    const gl = 
      canvas.getContext("webgl2", options) ||
      canvas.getContext("webgl", options) ||
      (canvas.getContext as any)("experimental-webgl", options);

    // Se o contexto foi criado, vamos garantir que ele não é um "zumbi"
    if (!gl) return false;

    // Checagem extra: Alguns drivers reportam o contexto mas não conseguem compilar nada
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      // Se o renderer for o SwiftShader (emulação por software), a gente pula fora
      if (renderer.toLowerCase().includes("swiftshader")) return false;
    }

    return true;
  } catch (e) {
    return false;
  }
}

const defaults = {
  defaultForm: {
    country: [], 
    genre: [],
    genreSpotify: [],
    stage: [1, 10],
    daysSinceFirstRelease: null,
    daysWithoutRelease: null,
    releasesLastYear: "",
  },
  defaultFilter: {
    country: [],
    genre: [],
    genreSpotify: [],
    stageInit: 1,
    stageEnd: 10,
    offset: 0,
    limit: 10,
  },
};

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_ARTIST_RANKING_FILTER);

  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);

    const { stageInit = 1, stageEnd = 10, ...rest } = parsed;
    return {
      defaultForm: {
        ...rest, 
        stage: [stageInit, stageEnd],
      },
      defaultFilter: parsed, 
    };
  }

  return defaults; 
};

const formatDate = (date: string) => date && moment(date).format("DD/MM/YYYY");

const LIMIT_ARTIST_GRAPH = 5000;


const ArtistNetwork: React.FC<any> = (props) => {
  const { defaultFilter, defaultForm } = initial();
  const savedIndex = localStorage.getItem(
    RADAR_ARTIST_RANKING_INDEX
  ) as ArtistsRankingIndex;
  const { history } = props;
  const [webglSupported, setWebglSupported] = useState<boolean>(() =>
    detectWebGL()
  );
  const [wrapperSize, setWrapperSize] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });
  const graphWrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = graphWrapperRef.current;
    if (!el) return;
    const update = () =>
      setWrapperSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [genreGraphOptions, setGenreGraphOptions] = useState<any>([]);
  const [artistGraphOptions, setArtistGraphOptions] = useState<any>([]);
  const [spotifyGenresRanking, setSpotifyGenresRanking] = useState<any>([]);
  const [genreSpotifyOptions, setGenreSpotifyOptions] = useState<any[]>([]);
  const [rawGraph, setRawGraph] = useState<any>();
  const [graphOptionsSelected, setGraphOptionsSelected] = useState<string>("");
  const [loadingGraph, setLoadingGraph] = useState<boolean>(true);
  const [proccessedGraph, setProccessedGraph] = useState<any>();
  const [rankingIndex, setRankingIndex] = useState<ArtistsRankingIndex>(
    savedIndex ? savedIndex : ArtistsRankingIndex.Performance
  );
  const [artistFilter, setArtistFilter] = useState("");
  const [panelsFading, setPanelsFading] = useState(false);
  const [graphSettling, setGraphSettling] = useState(false);
  const [pendingReveal, setPendingReveal] = useState(false);
  const sawSettlingRef = useRef(false);
  const panelFadeTimerRef = useRef<number | null>(null);
  const PANEL_FADE_MS = 200;
  const [subgraphDepth, setSubgraphDepth] = useState<1 | 2>(2);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSearch, setDrawerSearch] = useState("");
  const [subgraphGenresStrm, setSubgraphGenresStrm] = useState<string[]>([]);
  const [subgraphGenresSpotify, setSubgraphGenresSpotify] = useState<string[]>(
    []
  );
  const [genre, setGenre] = useState<string[]>(defaultForm.genre || []);
  const [genreSpotify, setGenreSpotify] = useState<string[]>(
    defaultForm.genreSpotify || []
  );
  const [country, setCountry] = useState<string[]>(defaultForm.country || []);
  const [stage, setStage] = useState<[number, number]>(
    defaultForm.stage || [1, 10]
  );
  const [daysSinceFirstRelease, setDaysSinceFirstRelease] = useState<
    number | undefined
  >(defaultForm.daysSinceFirstRelease ?? undefined);
  const [daysWithoutRelease, setDaysWithoutRelease] = useState<
    number | undefined
  >(defaultForm.daysWithoutRelease ?? undefined);
  const [releasesLastYear, setReleasesLastYear] = useState<any>(
    defaultForm.releasesLastYear ?? ""
  );
  const { t, i18n } = useTranslation("radar");

  const { genres, genreOptions } = useSelector((state: any) => state.genres);
  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );
  const genresSpotify = useSelector((state: any) => state.genres.genresSpotify);

  const onPanelApplyFilter = () => {
    const [stageInit, stageEnd] = stage;
    const saveFilter = {
      ...defaultFilter,
      country: country?.length === 0 ? undefined : country,
      daysSinceFirstRelease,
      genre,
      genreSpotify,
      releasesLastYear,
      stageInit,
      stageEnd,
    };
    localStorage.setItem(
      RADAR_ARTIST_RANKING_FILTER,
      JSON.stringify(saveFilter)
    );
    setFilter(saveFilter);
  };

  const hasActiveFilters =
    defaultForm.country?.length > 0 ||
    defaultForm.genre?.length > 0 ||
    defaultForm.genreSpotify?.length > 0 ||
    defaultForm.stage?.[0] !== 1 ||
    defaultForm.stage?.[1] !== 10 ||
    defaultForm.daysSinceFirstRelease != null ||
    defaultForm.daysWithoutRelease != null ||
    (defaultForm.releasesLastYear !== null && defaultForm.releasesLastYear !== "");

  const loggedUser = useSelector((state: any) => state.users.loggedUser);
  const admin = loggedUser.typeAccess === UserTypeEnum.AdminStrm;

  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);

  const wrappGetI18nCountry = (country: string) =>
    getI18nCoutry(country, i18n, countries);

  const daysOptions = [
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 1 }),
      value: 365,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 2 }),
      value: 730,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 3 }),
      value: 1095,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 4 }),
      value: 1460,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 5 }),
      value: 1825,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 10 }),
      value: 3650,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 15 }),
      value: 5475,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 20 }),
      value: 7300,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 30 }),
      value: 10950,
    },
  ];

  const daysWithoutReleaseOptions = [
    { label: 1, value: 365 },
    { label: 2, value: 730 },
    { label: 3, value: 1095 },
    { label: 4, value: 1460 },
    { label: 5, value: 1825 },
    { label: 10, value: 3650 },
    { label: 15, value: 5475 },
    { label: 20, value: 7300 },
    { label: 30, value: 10950 },
  ];

  const colunasGeneros: ColumnsType<any> = [
    {
      title: t("pages.ArtistsRanking.genre"),
      render: (value: any) => {
        return (
          <span style={{ color: `${value.color}` }}>{value.genre_spotify}</span>
        );
      },
    },
    {
      title: t("pages.ArtistsRanking.percentage"),
      dataIndex: "percent",
      align: "center",
      render: (value: any) => <>{value} %</>,
    },
    {
      title: t("pages.ArtistsRanking.quantity"),
      align: "center",
      dataIndex: "size",
    },
  ];
  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ArtistsRanking.artistName"),
      dataIndex: "artistName",
      width: "15%",
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "15%",
      render: (value) =>
        value.map((genre: any) => (
          <Tag key={Math.random()}>{wrappGetI18nGenre(genre)}</Tag>
        )),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      align: "center",
      width: "10%",
      render: (value: string) => <>{wrappGetI18nCountry(value)}</>,
    },
    {
      title: t("pages.ArtistsRanking.stage"),
      dataIndex: "careerStage",
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.development"),
      dataIndex: "developmentIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.performance"),
      dataIndex: "performanceIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
  ];

  if (admin) {
    colunas.push({
      key: "acao",
      width: "5%",
      align: "right",
      render: (_, record) => (
        <a
          href={`/artist-profile/${record.artistIdStrm}`}
          style={{ color: "white" }}
          target="_blank"
          rel="noreferrer"
          onClick={(evt) => evt.stopPropagation()}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      ),
    });
  }

  const onDeleteClick = () => {
    setGenre([]);
    setGenreSpotify([]);
    setCountry([]);
    setStage([1, 10]);
    setDaysSinceFirstRelease(undefined);
    setDaysWithoutRelease(undefined);
    setReleasesLastYear("");
    setFilter(defaults.defaultFilter);
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    localStorage.setItem(RADAR_ARTIST_RANKING_INDEX, saveRankingIndex);
    setRankingIndex(saveRankingIndex);
  };

  const onChangeHightlightArtist = (value: string) => {
    const next = value || "";
    if (next === artistFilter) return;
    if (panelFadeTimerRef.current) window.clearTimeout(panelFadeTimerRef.current);
    setPanelsFading(true);
    sawSettlingRef.current = false;
    panelFadeTimerRef.current = window.setTimeout(() => {
      unstable_batchedUpdates(() => {
        setArtistFilter(next);
        setPendingReveal(true);
      });
      panelFadeTimerRef.current = null;
    }, PANEL_FADE_MS);
  };

  useEffect(() => {
    if (!pendingReveal) return;
    if (!webglSupported) {
      setPanelsFading(false);
      setPendingReveal(false);
      return;
    }
    if (graphSettling) {
      sawSettlingRef.current = true;
      return;
    }
    if (sawSettlingRef.current) {
      setPanelsFading(false);
      setPendingReveal(false);
      sawSettlingRef.current = false;
    }
  }, [pendingReveal, graphSettling, webglSupported]);

  useEffect(
    () => () => {
      if (panelFadeTimerRef.current) window.clearTimeout(panelFadeTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (rawGraph) {
      setLoadingGraph(true);
      setSpotifyGenresRanking(rawGraph?.genres_spotify || []);
      setGenreGraphOptions(
        rawGraph?.genres_spotify?.map(({ genre_spotify }: any) => ({
          id: genre_spotify,
          value: genre_spotify,
        })) || []
      );
      setProccessedGraph({
        nodes:
          Object.keys(rawGraph?.nodes || []).map((key: string) => ({
            ...rawGraph.nodes[key],
            id: key,
          })) || [],
        links:
          rawGraph.edges?.map(([source, target]: [any, any]) => ({
            source,
            target,
          })) || [],
      });
      setArtistGraphOptions(
        Object.keys(rawGraph?.nodes || []).map(
          (key: string) => rawGraph.nodes[key].artist_name
        )
      );
      setLoadingGraph(false);
    }
  }, [rawGraph]);

  const onChangeGraphOptions = (value: string) => {
    setGraphOptionsSelected(value || "");
  };

  useEffect(() => {
    const getArtistNetworkGraph = async (limit: number) => {
      setLoadingGraph(true);
      const result = await fetchArtistGraph(
        {
          ...filter,
          limit: limit,
          offset: 0,
        },
        rankingIndex
      );
      setRawGraph(result);
      setLoadingGraph(false);
    };

    getArtistNetworkGraph(webglSupported ? LIMIT_ARTIST_GRAPH : 2000);
  }, [filter, rankingIndex, webglSupported]);

  const anchoredSubgraph = useMemo(() => {
    if (!artistFilter || !rawGraph) return undefined;

    const target = artistFilter.toLowerCase();
    const nodesById = rawGraph.nodes || {};
    const anchorId = Object.keys(nodesById).find(
      (key: string) => nodesById[key].artist_name?.toLowerCase() === target
    );
    if (!anchorId) return undefined;

    const edges: [string, string][] = rawGraph.edges || [];

    const hop1 = new Set<string>();
    edges.forEach(([source, target]) => {
      if (source === anchorId) hop1.add(target);
      if (target === anchorId) hop1.add(source);
    });

    const keepIds = new Set<string>([anchorId, ...Array.from(hop1)]);
    if (subgraphDepth === 2) {
      edges.forEach(([source, target]) => {
        if (hop1.has(source) && target !== anchorId && !hop1.has(target)) {
          keepIds.add(target);
        }
        if (hop1.has(target) && source !== anchorId && !hop1.has(source)) {
          keepIds.add(source);
        }
      });
    }

    return {
      nodes: Array.from(keepIds).map((key) => ({
        ...nodesById[key],
        id: key,
      })),
      links: edges
        .filter(([s, t]) => keepIds.has(s) && keepIds.has(t))
        .map(([source, target]) => ({ source, target })),
    };
  }, [artistFilter, rawGraph, subgraphDepth]);

  useEffect(() => {
    if (!artistFilter || !rawGraph || loadingGraph) return;
    if (anchoredSubgraph) return;
    message.warning(
      t("pages.ArtistsRanking.artistNotInFilter", { artist: artistFilter })
    );
    setArtistFilter("");
  }, [anchoredSubgraph, artistFilter, rawGraph, loadingGraph, t]);

  useEffect(() => {
    setSubgraphGenresStrm([]);
    setSubgraphGenresSpotify([]);
    if (!artistFilter) setDrawerOpen(false);
  }, [artistFilter]);

  const subgraphGenreStrmOptions = useMemo(() => {
    if (!anchoredSubgraph) return [];
    const set = new Set<string>();
    anchoredSubgraph.nodes.forEach((n: any) => {
      n.genres_strm?.forEach((g: string) => set.add(g));
    });
    return Array.from(set)
      .map((g) => ({ label: wrappGetI18nGenre(g), value: g }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchoredSubgraph, i18n.language]);

  const subgraphGenreSpotifyOptions = useMemo(() => {
    if (!anchoredSubgraph) return [];
    const set = new Set<string>();
    anchoredSubgraph.nodes.forEach((n: any) => {
      n.genres_spotify?.forEach((g: string) => set.add(g));
    });
    return Array.from(set)
      .sort()
      .map((g) => ({ label: g, value: g }));
  }, [anchoredSubgraph]);

  const filteredSubgraph = useMemo(() => {
    if (!anchoredSubgraph) return undefined;
    if (!subgraphGenresStrm.length && !subgraphGenresSpotify.length) {
      return anchoredSubgraph;
    }
    const anchorName = artistFilter.toLowerCase();
    const keepIds = new Set<string>();
    anchoredSubgraph.nodes.forEach((n: any) => {
      const isAnchor = n.artist_name?.toLowerCase() === anchorName;
      if (isAnchor) {
        keepIds.add(n.id);
        return;
      }
      const matchesStrm =
        !subgraphGenresStrm.length ||
        n.genres_strm?.some((g: string) => subgraphGenresStrm.includes(g));
      const matchesSpotify =
        !subgraphGenresSpotify.length ||
        n.genres_spotify?.some((g: string) =>
          subgraphGenresSpotify.includes(g)
        );
      if (matchesStrm && matchesSpotify) keepIds.add(n.id);
    });
    return {
      nodes: anchoredSubgraph.nodes.filter((n: any) => keepIds.has(n.id)),
      links: anchoredSubgraph.links.filter(
        (l: any) => keepIds.has(l.source) && keepIds.has(l.target)
      ),
    };
  }, [
    anchoredSubgraph,
    subgraphGenresStrm,
    subgraphGenresSpotify,
    artistFilter,
  ]);

  const subgraphFiltersNoMatch =
    (subgraphGenresStrm.length > 0 || subgraphGenresSpotify.length > 0) &&
    !!filteredSubgraph &&
    filteredSubgraph.nodes.length <= 1;

  const displayGraph = artistFilter
    ? subgraphFiltersNoMatch
      ? anchoredSubgraph
      : filteredSubgraph
    : proccessedGraph;

  useEffect(() => {
    if (subgraphFiltersNoMatch) {
      message.warning(t("pages.ArtistsRanking.subgraphFiltersNoMatch"));
    }
  }, [subgraphFiltersNoMatch, t]);

  const hopSets = useMemo(() => {
    const empty = {
      hop1: new Set<string>(),
      hop2: new Set<string>(),
      bridges: new Map<string, string[]>(),
    };
    if (!artistFilter || !rawGraph) return empty;
    const target = artistFilter.toLowerCase();
    const nodesById = rawGraph.nodes || {};
    const anchorId = Object.keys(nodesById).find(
      (key: string) => nodesById[key].artist_name?.toLowerCase() === target
    );
    if (!anchorId) return empty;

    const edges: [string, string][] = rawGraph.edges || [];
    const hop1 = new Set<string>();
    edges.forEach(([s, t]) => {
      if (s === anchorId) hop1.add(t);
      if (t === anchorId) hop1.add(s);
    });
    const hop2 = new Set<string>();
    const bridges = new Map<string, Set<string>>();
    const addBridge = (hop2Id: string, hop1Id: string) => {
      if (!bridges.has(hop2Id)) bridges.set(hop2Id, new Set());
      bridges.get(hop2Id)!.add(hop1Id);
    };
    edges.forEach(([s, t]) => {
      if (hop1.has(s) && t !== anchorId && !hop1.has(t)) {
        hop2.add(t);
        addBridge(t, s);
      }
      if (hop1.has(t) && s !== anchorId && !hop1.has(s)) {
        hop2.add(s);
        addBridge(s, t);
      }
    });

    const bridgeNames = new Map<string, string[]>();
    bridges.forEach((hop1Ids, hop2Id) => {
      const names = Array.from(hop1Ids)
        .map((id) => nodesById[id]?.artist_name)
        .filter(Boolean) as string[];
      bridgeNames.set(hop2Id, names);
    });
    return { hop1, hop2, bridges: bridgeNames };
  }, [artistFilter, rawGraph]);

  const relatedArtists = useMemo(() => {
    if (!filteredSubgraph || !artistFilter) return [];
    const anchorName = artistFilter.toLowerCase();
    return filteredSubgraph.nodes
      .filter((n: any) => n.artist_name?.toLowerCase() !== anchorName)
      .map((n: any) => ({
        ...n,
        hop: hopSets.hop1.has(n.id) ? 1 : hopSets.hop2.has(n.id) ? 2 : null,
        bridges: hopSets.bridges.get(n.id) || [],
      }))
      .sort((a: any, b: any) => {
        const hopA = a.hop ?? 99;
        const hopB = b.hop ?? 99;
        if (hopA !== hopB) return hopA - hopB;
        return (b.career_stage ?? 0) - (a.career_stage ?? 0);
      });
  }, [filteredSubgraph, artistFilter, hopSets]);

  const relatedCounts = useMemo(
    () => ({
      direct: relatedArtists.filter((a: any) => a.hop === 1).length,
      secondDegree: relatedArtists.filter((a: any) => a.hop !== null).length,
    }),
    [relatedArtists]
  );

  const filteredRelatedArtists = useMemo(() => {
    if (!drawerSearch.trim()) return relatedArtists;
    const normalized = drawerSearch.trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    return relatedArtists.filter((a: any) => {
      const name = (a.artist_name ?? "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
      return name.includes(normalized);
    });
  }, [relatedArtists, drawerSearch]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_ARTIST_NETWORK,
    });
  }, []);

  useEffect(() => {
    setGenreSpotifyOptions(
      genresSpotify
        .map((genre: any) => ({
          label: genre,
          value: genre,
        }))
        .sort((a: any, b: any) =>
          a.label > b.label ? 1 : a.label < b.label ? -1 : 0
        )
    );
  }, [genresSpotify]);

  const topRightPanel = artistFilter ? (
    <motion.div
      className="controls-panel-fade-stack controls-panel-fade-stack--right"
      animate={{ opacity: panelsFading ? 0 : 1 }}
      transition={{ duration: PANEL_FADE_MS / 1000, ease: "easeInOut" }}
      style={{ pointerEvents: panelsFading ? "none" : "auto" }}
    >
      <ControlsPanel
        title={t("pages.ArtistsRanking.subgraphDepth")}
        defaultOpen={true}
      >
        <Select
          value={subgraphDepth}
          onChange={(v) => setSubgraphDepth(v as 1 | 2)}
          options={[
            {
              label: t("pages.ArtistsRanking.subgraphDepthDirect"),
              value: 1,
            },
            {
              label: t("pages.ArtistsRanking.subgraphDepthSecondDegree"),
              value: 2,
            },
          ]}
          style={{ width: "100%" }}
        />
      </ControlsPanel>
      <button
        type="button"
        className="buton-control-panel-custom-graph"
        onClick={() => setDrawerOpen(true)}
      >
        <EyeOutlined />
        {t("pages.ArtistsRanking.exibir")}
      </button>
    </motion.div>
  ) : null;

  const controlsPanel = (
    <motion.div
      className="controls-panel-fade-stack"
      animate={{ opacity: panelsFading ? 0 : 1 }}
      transition={{ duration: PANEL_FADE_MS / 1000, ease: "easeInOut" }}
      style={{ pointerEvents: panelsFading ? "none" : "auto" }}
    >
      {artistFilter && (
        <button
          type="button"
          className="buton-control-panel-custom-graph"
          onClick={() => onChangeHightlightArtist("")}
        >
          <LeftOutlined />
          {t("pages.ArtistsRanking.backToGraph")}
        </button>
      )}
      <ControlsPanel title={t("pages.ArtistsRanking.artists")} defaultOpen={true}>
        <ControlSection label={t("pages.ArtistsRanking.artistHighlight")}>
          <Select
            value={artistFilter || undefined}
            showArrow={false}
            onClear={() => onChangeHightlightArtist("")}
            optionFilterProp="value"
            allowClear
            showSearch
            onSelect={onChangeHightlightArtist}
            options={artistGraphOptions?.map((option: any) => ({
              label: option,
              value: option,
            }))}
            placeholder={t("pages.ArtistsRanking.selectHere")}
            style={{ width: "100%" }}
          />
        </ControlSection>
        <ControlSection label={t("pages.ArtistsRanking.genre")}>
          <Select
            onChange={onChangeGraphOptions}
            options={genreGraphOptions}
            placeholder={t("pages.ArtistsRanking.selectHere")}
            showArrow={false}
            showSearch
            allowClear
            style={{ width: "100%" }}
          />
        </ControlSection>
      </ControlsPanel>
      {artistFilter && (
        <ControlsPanel
          title={t("pages.ArtistsRanking.subgraphFilter")}
          defaultOpen={false}
        >
          <ControlSection label={t("pages.ArtistsRanking.genre")}>
            <Select
              mode="multiple"
              value={subgraphGenresStrm}
              onChange={(v) => setSubgraphGenresStrm(v as string[])}
              options={subgraphGenreStrmOptions}
              filterOption={filterOptionDiacritics}
              maxTagCount="responsive"
              allowClear
              placeholder={t("pages.ArtistsRanking.selectHere")}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.genreSpotify")}>
            <Select
              mode="multiple"
              value={subgraphGenresSpotify}
              onChange={(v) => setSubgraphGenresSpotify(v as string[])}
              options={subgraphGenreSpotifyOptions}
              filterOption={(input, option) =>
                (option?.value as string).toUpperCase().includes(input.toUpperCase())
              }
              maxTagCount="responsive"
              allowClear
              placeholder={t("pages.ArtistsRanking.selectHere")}
              style={{ width: "100%" }}
            />
          </ControlSection>
          {(subgraphGenresStrm.length > 0 || subgraphGenresSpotify.length > 0) && (
            <div className="filter-button">
              <span
                className="delete"
                onClick={() => {
                  setSubgraphGenresStrm([]);
                  setSubgraphGenresSpotify([]);
                }}
              >
                {t("pages.ArtistsRanking.subgraphFilterClear")}
              </span>
            </div>
          )}
        </ControlsPanel>
      )}
      {!artistFilter && (
        <ControlsPanel title={t("pages.ArtistsRanking.filters")} defaultOpen={hasActiveFilters}>
          <ControlSection label={t("pages.ArtistsRanking.genre")}>
            <Select
              mode="multiple"
              value={genre}
              onChange={setGenre}
              options={genreOptions}
              filterOption={filterOptionDiacritics}
              placeholder={t("pages.ArtistsRanking.selectHere")}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.genreSpotify")}>
            <Select
              mode="multiple"
              value={genreSpotify}
              onChange={setGenreSpotify}
              options={genreSpotifyOptions}
              filterOption={(input, option) =>
                (option?.value as string).toUpperCase().includes(input.toUpperCase())
              }
              maxTagCount="responsive"
              allowClear
              placeholder={t("pages.ArtistsRanking.selectHere")}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.country")}>
            <Select
              mode="multiple"
              value={country}
              onChange={setCountry}
              options={countryOptions}
              filterOption={(input, option) =>
                (option?.label as string).toUpperCase().includes(input.toUpperCase())
              }
              placeholder={t("pages.ArtistsRanking.selectHere")}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.stage")}>
            <Slider
              min={1}
              max={10}
              range
              value={stage}
              onChange={(v) => setStage(v as [number, number])}
              marks={{
                1: { style: { color: "#fff" }, label: <strong>1</strong> },
                10: { style: { color: "#fff" }, label: <strong>10</strong> },
              }}
              tooltipPlacement="bottom"
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.daysSinceFirstRelease")}>
            <Select
              value={daysSinceFirstRelease}
              onChange={(v) => setDaysSinceFirstRelease(v == null ? undefined : v)}
              options={daysOptions}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("pages.ArtistsRanking.selectHere")}
              showArrow={false}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.daysWithoutRelease")}>
            <Select
              value={daysWithoutRelease}
              onChange={(v) => setDaysWithoutRelease(v == null ? undefined : v)}
              options={daysWithoutReleaseOptions}
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("pages.ArtistsRanking.selectHere")}
              showArrow={false}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <ControlSection label={t("pages.ArtistsRanking.releasesInThePastYear")}>
            <InputNumber
              min={0}
              value={releasesLastYear}
              onChange={setReleasesLastYear}
              placeholder={t("pages.ArtistsRanking.typeHere")}
              style={{ width: "100%" }}
            />
          </ControlSection>
          <div className="filter-button">
            <span className="delete" onClick={onDeleteClick}>
              {t("pages.ArtistsRanking.delete")}
            </span>
            <button
              type="button"
              className="buton-control-panel-custom-graph"
              onClick={onPanelApplyFilter}
            >
              {t("pages.ArtistsRanking.filter")}
            </button>
          </div>
        </ControlsPanel>
      )}
    </motion.div>
  );

  return (
    <div className="radar-home" style={{ color: "white" }}>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="artist" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <div className="radar-home-flex-graphs-controls-container">
            <div style={{ textAlign: "center" }}>
              <Radio.Group value="network">
                <Radio.Button
                  value="ranking"
                  onClick={() => history.push("/artist-ranking")}
                >
                  {t("pages.ArtistsRanking.title")}
                </Radio.Button>
                <Radio.Button
                  value="network"
                  onClick={() => history.push("/artist-network")}
                >
                  {t("pages.ArtistsRanking.artistNetwork")}
                </Radio.Button>
              </Radio.Group>

            </div>
             <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
                <Radio.Button value={ArtistsRankingIndex.Performance}>
                  <Tooltip
                    title={t("pages.ArtistsRanking.performanceExplaination")}
                    overlayStyle={{ width: 500 }}
                  >
                    {t("pages.ArtistsRanking.performance")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.DeltaSpotify}>
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t("pages.ArtistsRanking.deltaSpotifyExplaination")}
                  >
                    {t("pages.ArtistsRanking.spotifyDelta")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button
                  value={ArtistsRankingIndex.PerformanceDevelopment}
                >
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t(
                      "pages.ArtistsRanking.performanceDevelopmentExplaination"
                    )}
                  >
                    {t("pages.ArtistsRanking.performanceDevelopment")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.GrowthSpeed}>
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t("pages.ArtistsRanking.growthSpeedExplaination")}
                  >
                    {t("pages.ArtistsRanking.growthSpeed")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.Volatility}>
                  <Tooltip
                    overlayStyle={{ maxWidth: 550 }}
                    title={t("pages.ArtistsRanking.volatilityExplaination")}
                  >
                    {t("pages.ArtistsRanking.volatility")}
                  </Tooltip>
                </Radio.Button>
              </Radio.Group>
          </div>

          <div className="radar-home-flex-graphs-results-container">
            <div>
              <h4
              >
                <div className="container-warning">
                  <Typography.Title level={4} className="title">
                    {t("pages.ArtistsRanking.results", {
                      total: ptBRFormat(
                        webglSupported ? LIMIT_ARTIST_GRAPH : 2000
                      ),
                    })}
                  </Typography.Title>
                </div>
              </h4>
            </div>
            <div>
              <h5
              >
               
                  {formatDate(rawGraph?.ranking_date) && (
                    <>
                      {t("pages.ArtistsRanking.lastUpdate")}
                      <b>{`${formatDate(rawGraph?.ranking_date)} `}</b>
                    </>
                  )}
            
              </h5>
            </div>
          </div>
          <div>
            <div>
              <div className="legenda-artist-network">
                <span className="caixa" style={{ backgroundColor: "#EFEFEF" }}>
  
                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.normalNode")}
                </span>

                <span className="caixa" style={{ backgroundColor: "#5A57FF" }}>
                  </span>
                <span className="label">
                  {t("pages.ArtistsRanking.highlightedNode")}
                </span>

                <span className="caixa" style={{ backgroundColor: "#5554B2" }}>
    
                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.relatedSelectedNodes")}
                </span>

                <span className="caixa" style={{ backgroundColor: "#8AE79A" }}>

                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.selectedGroupNode")}
                </span>
              </div>
            </div>
            <div>
              <div ref={graphWrapperRef} className="artist-network-graph-wrapper" style={{ height: "100vh" }}>
                {!webglSupported ? (
                  <>
                    <ArtistGraphCanvas
                      data={displayGraph}
                      loading={loadingGraph}
                      highlightArtist={artistFilter}
                      selectedGenre={graphOptionsSelected}
                      onArtistClick={onChangeHightlightArtist}
                      width={wrapperSize.w}
                      height={wrapperSize.h}
                    />
                    {!loadingGraph && (
                      <div className="controls-panel-stack">{controlsPanel}</div>
                    )}
                    {!loadingGraph && topRightPanel && (
                      <div className="controls-panel-stack controls-panel-stack--right">
                        {topRightPanel}
                      </div>
                    )}
                  </>
                ) : (
                  <ArtistGraphThree
                    data={displayGraph}
                    loading={loadingGraph}
                    highlightArtist={artistFilter}
                    selectedGenre={graphOptionsSelected}
                    onArtistClick={onChangeHightlightArtist}
                    onWebGLError={() => setWebglSupported(false)}
                    onSettlingChange={setGraphSettling}
                    topRightControls={topRightPanel}
                    controls={controlsPanel}
                  />
                )}

                   


              </div>
            </div>
            <Drawer
              open={drawerOpen}
              onClose={() => { setDrawerOpen(false); setDrawerSearch(""); }}
              title={
                <div className="title-row">
                  <div className="title-related-count">
                  <span className="title-name">
                    {artistFilter
                      ? `${t("pages.ArtistsRanking.exibir")} — ${artistFilter}`
                      : t("pages.ArtistsRanking.exibir")}
                  </span>
                  {artistFilter && (
                    <span className="title-counts">
                      <span className="title-count-pill">
                        <strong>{relatedCounts.direct}</strong>
                        {t("pages.ArtistsRanking.directConnections")}
                      </span>
                      {subgraphDepth === 2 && (
                        <span className="title-count-pill">
                          <strong>{relatedCounts.secondDegree}</strong>
                          {t("pages.ArtistsRanking.secondDegreeConnections")}
                        </span>
                      )}
                      {subgraphGenresStrm.map((g) => (
                        <span key={`f-strm-${g}`} className="title-count-pill title-count-pill-active">
                          <strong>{wrappGetI18nGenre(g)}</strong>
                        </span>
                      ))}
                      {subgraphGenresSpotify.map((g) => (
                        <span key={`f-sp-${g}`} className="title-count-pill title-count-pill-active">
                          <strong>{g}</strong>
                        </span>
                      ))}
                    </span>
                  )}
                  </div>
                    {relatedArtists.length > 0 && (
                      <span>
                <input
                  className="drawer-search-input"
                  type="text"
                  placeholder={t("pages.ArtistsRanking.searchArtist")}
                  value={drawerSearch}
                  onChange={(e) => setDrawerSearch(e.target.value)}
                />
                </span>
              )}
                </div>
              }
            >
            
              {filteredRelatedArtists.length === 0 ? (
                <div className="drawer-empty-state">
                  {t("pages.ArtistsRanking.noRelatedArtists")}
                </div>
              ) : (
                <ul className="drawer-artist-list">
                  {filteredRelatedArtists.map((artist: any) => (
                    <li key={artist.id}>
                      <div
                       className="drawer-artist-item"
                        onClick={() => {
                          onChangeHightlightArtist(artist.artist_name);
                          setDrawerOpen(false);
                        }}
                      > <div>
                        <div className="drawer-artist-row">
                         
                            <span className="drawer-artist-name-group">
                              <span className="drawer-artist-name">
                                {artist.artist_name}
                              </span>
                              {artist.hop && (
                                <span className={`hop-badge ${artist.hop === 1 ? "hop-direct" : "hop-second"}`}>
                                  {artist.hop === 1
                                    ? t("pages.ArtistsRanking.directConnection")
                                    : t("pages.ArtistsRanking.secondDegreeConnections")}
                                </span>
                              )}
                            </span>
                            <span className="drawer-artist-stage">
                              Stage {artist.career_stage ?? "-"}
                            </span>
                                                    </div>
                                                    <div>
                            {artist.hop === 2 && artist.bridges.length > 0 && (
                              <span className="bridge-label">
                                {t("pages.ArtistsRanking.relatedWith")}{" "}
                                <strong>
                                  {artist.bridges.join(", ")}
                                </strong>
                              </span>
                            )}
                                                    </div>
                          </div>
                        {(artist.genres_spotify?.length > 0 ||
                          artist.genres_strm?.length > 0) && (
                          <div className="drawer-artist-genres">
                            {artist.genres_spotify?.length > 0 && (
                              <div className="drawer-genre-row">
                                <span className="drawer-genre-label">Spotify:</span>
                                {artist.genres_spotify.map((g: string) => (
                                  <span key={`sp-${artist.id}-${g}`} className="genre-badge spotify">{g}</span>
                                ))}
                              </div>
                            )}
                            {artist.genres_strm?.length > 0 && (
                              <div className="drawer-genre-row">
                                <span className="drawer-genre-label">STRM:</span>
                                {artist.genres_strm.map((g: string) => (
                                  <span key={`strm-${artist.id}-${g}`} className="genre-badge strm">{wrappGetI18nGenre(g)}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Drawer>

            <Col xs={24}>
              <Table
                columns={colunasGeneros}
                dataSource={spotifyGenresRanking || []}
                rowKey="genre_spotify"
                loading={loadingGraph}
                style={{ marginTop: 48 }}
              />
            </Col>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ArtistNetwork;
